import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { IoIosHeartEmpty, IoIosHeart } from "react-icons/io";
import { Tooltip, Avatar, Box, Button, Spacer, Flex, Center, VStack, Heading, HStack, IconButton, Stack, Text, Image, Skeleton, Circle } from "@chakra-ui/react";
import { shortenAddress } from "@usedapp/core";
import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Slider from "react-slick";
import { NftResource } from "../../../components/NftResource";
import { I18N_NAMESPACE } from "../../../core/constants/i18n";
import { images } from "../../../core/constants/images";
import { MINT_TYPE } from "../../../core/constants/nft";
import { useRoyaltyInfo } from "../../../core/hooks/useRoyaltyInfo";
import { ICollection } from "../../../core/models/collection";
import { INft, INftMetadata } from "../../../core/models/nft";
import { IUser } from "../../../core/models/user";
import { uriToHttps } from "../../../core/utils/ipfs";
import { formatNumber } from "../../../core/utils/number";
import { collectionService } from "../../../services/collection.service";
import { userService } from "../../../services/user.service";
import { useAppSelector } from "../../../store/hook";
import { nftService } from "../../../services/nft.service";
import { colors } from "../../../core/constants/colors";

interface Props {
  nfts: INft[];
}

const NextArrow = (props: any) => {
  return (
    <IconButton
      borderRadius={"full"}
      variant="outline"
      aria-label="Call Sage"
      fontSize="20px"
      top={"42%"}
      right={"-8px"}
      background={"#fff"}
      position="absolute"
      onClick={props.onClick}
      icon={<ChevronRightIcon fontSize="2xl" />}
    />
  );
};

const PrevArrow = (props: any) => {
  return (
    <IconButton
      borderRadius={"full"}
      variant="outline"
      aria-label="Call Sage"
      fontSize="20px"
      top={"42%"}
      left={"-8px"}
      background={"#fff"}
      position="absolute"
      zIndex={1}
      onClick={props.onClick}
      icon={<ChevronLeftIcon fontSize="2xl" />}
    />
  );
};

const FavoriteNft = ({ nft }: { nft: INft }) => {
  const navigate = useNavigate();
  const { t } = useTranslation([I18N_NAMESPACE.DASHBOARD]);
  const supportedTokens = useAppSelector((state: any) => state.token.supportedTokens);
  const token = useMemo(
    () => (nft.asset_type?.token_address ? supportedTokens.byId[nft.asset_type?.token_address] : null),
    [supportedTokens, nft.asset_type?.token_address]
  );

  const royaltyInfo = useRoyaltyInfo(nft.token_address, nft.token_id);

  const [creator, setCreator] = useState<IUser>();
  const [collection, setCollection] = useState<ICollection>();
  const [metadata, setMetata] = useState<INftMetadata>({
    image: nft.image,
    name: nft.name,
    animation_url: nft.animation_url,
  });

  const [favorites, setFavorites] = useState<number>(nft.favorites ?? 0);

  const [isLoadingCreator, setIsLoadingCreator] = useState(false);
  const [isLoadingCollection, setIsLoadingCollection] = useState(false);

  const onClick = useCallback(() => {
    navigate(`assets/${nft.token_address}/${nft.token_id}`);
  }, [navigate, nft.token_address, nft.token_id]);

  useEffect(() => {
    async function getMetadata() {
      if (nft.token_uri) {
        const uri = uriToHttps(nft.token_uri);
        if (!uri) return;

        const result = await axios.get(uri);
        setMetata(result.data);
      }
    }

    getMetadata();
  }, [nft.token_uri]);

  useEffect(() => {
    setIsLoadingCollection(true);
    async function getCollection() {
      const response = await collectionService.getDetail(nft.token_address);
      setCollection(response.data);
      setIsLoadingCollection(false);
    }
    getCollection();
  }, [nft.token_address]);

  useEffect(() => {
    async function getCreator() {
      const id =
        nft.mint_type === MINT_TYPE.LAZY && nft?.royalties && nft.royalties.length > 0
          ? nft.royalties[0].account
          : royaltyInfo?.receiver;
      if (!id) return;
      const creatorResponse = await userService.getProfileById(id);
      setCreator(creatorResponse.data);
      setIsLoadingCreator(false);
    }

    setIsLoadingCreator(true);
    getCreator();
  }, [royaltyInfo?.receiver, nft.mint_type, nft.royalties]);


  useEffect(() => {
    const getNumberFavorites = async () => {
      const result = await nftService.getNumberFavorite(nft.token_address, nft.token_id);
      if (result?.data !== null && result?.data !== undefined) {
        setFavorites(result.data);
      }
    };

    getNumberFavorites();
  }, [nft.token_address, nft.token_id]);

  return (
    <Box padding={4}>
      <VStack
        padding={4}
        spacing={4}
        borderWidth={1}
        borderRadius="md"
        _hover={{ borderColor: "gray.300" }}
        align="left"
        justify="stretch"
        overflow="hidden">

        {/* Creator & Collection Stack */}
        <HStack spacing={-2}>
          <Tooltip label={`${t("Collection")}: ${nft.token_name}`}>
            <Skeleton isLoaded={!isLoadingCollection} borderRadius="full">
              <Image
                boxSize={"32px"}
                borderRadius="full"
                fallbackSrc={images.collectionLogoDefault}
                src={collection?.logo}
                onClick={(e: any) => { e.preventDefault(); navigate(`/collections/${nft.token_address}`) }}
                _hover={{ shadow: "md", transition: "all 0.1s ease 0s", transform: "translateY(-2px)", zIndex: 9999 }}
              />
            </Skeleton>
          </Tooltip>

          <Tooltip label={`${t("Creator")}: ${creator?.display_name || (creator?.id && shortenAddress(creator.id))}`}>
            <Skeleton isLoaded={!isLoadingCreator} borderRadius="full">
              <Avatar
                size={"sm"}
                src={creator?.image || undefined}
                onClick={(e: any) => { e.preventDefault(); navigate(`/profile/collections/${creator?.id}`) }}
                _hover={{ shadow: "md", transition: "all 0.1s ease 0s", transform: "translateY(-2px)", zIndex: 9999 }}
              />
            </Skeleton>
          </Tooltip>
        </HStack>

        <Box flex={1} borderRadius="lg" overflow="hidden" onClick={onClick} cursor="pointer">
          <NftResource image={metadata.image} animationUrl={metadata.animation_url} muted
            maxW="480px"
            maxH="240px" />
        </Box>

        <Text fontWeight={700} fontSize="xl">
          {metadata.name}
        </Text>
        <HStack>
          <Text color={colors.primary} fontSize={"xl"} fontWeight={700}>
            {token?.decimals ? formatNumber(nft.price / Math.pow(10, token?.decimals)) : ""} {token?.symbol}
          </Text>
          <Spacer flex={1} />
          <HStack spacing={1}>
            <IoIosHeartEmpty color="gray" size={16} />
            {(favorites && favorites > 0) &&
              (<Text textColor="gray" fontSize={"md"}>{favorites}</Text>)}
          </HStack>
        </HStack>
      </VStack>
    </Box>
  );
};

export const FeaturedNftSlider = React.memo((props: Props) => {
  return (
    <Slider
      infinite
      speed={500}
      autoplaySpeed={5000}
      slidesToShow={1}
      slidesToScroll={1}
      autoplay
      nextArrow={<NextArrow />}
      prevArrow={<PrevArrow />}
    >
      {props.nfts.map((nft) => (
        <FavoriteNft nft={nft} key={nft.id} />
      ))}
    </Slider>
  );
});
